import React, {
    createContext,
    useContext,
    useReducer,
    useMemo,
    useEffect,
    useRef,
} from 'react'
import {
    ChartOptions,
    EVPData,
    Settings,
    strikeZoneTypes,
} from '../schema/types.ts'
import getScales from '../Chart/Helpers/get-scales.ts'
import shouldShowNormalized from '../Chart/Helpers/StrikeZones/should-show-normalized.ts'
import { dynamicNormalizationFeet } from '../Chart/Helpers/get-pitch-normal-relative-scales.ts'
import getStrikeZoneDims from '../Chart/Helpers/StrikeZones/get-strike-zone-dims.ts'
import getStrikeZoneType from '../Chart/Helpers/StrikeZones/get-strike-zone-type.ts'

type ChartState = {
    chartOptions: ChartOptions[]
    settings: Settings
    zoom: number
    translate: { x: number; y: number }
}

type ChartAction =
    | {
          type: 'UPDATE_CHART_OPTION'
          index: number
          updates: Partial<ChartOptions>
      }
    | { type: 'SET_ZOOM'; zoom: number; translate: { x: number; y: number } }
    | { type: 'CYCLE_STRIKE_ZONE'; index: number; canBePersonal: boolean }
    | { type: 'RESET_CHART_SETTINGS'; settings: Settings }

type ChartContextType = {
    state: ChartState
    dispatch: React.Dispatch<ChartAction>
    tooltipRef: React.RefObject<SVGGElement>
}

const ChartContext = createContext<ChartContextType | undefined>(undefined)

function chartReducer(state: ChartState, action: ChartAction): ChartState {
    switch (action.type) {
        case 'UPDATE_CHART_OPTION': {
            const newOptions = [...state.chartOptions]
            newOptions[action.index] = {
                ...newOptions[action.index],
                ...action.updates,
            }
            return { ...state, chartOptions: newOptions }
        }
        case 'CYCLE_STRIKE_ZONE': {
            const noPersonal = strikeZoneTypes.filter((d) => d !== 'personal')
            const types = action.canBePersonal ? strikeZoneTypes : noPersonal
            const newOptions = [...state.chartOptions]
            const current = newOptions[action.index].strikeZone || 'generic'
            const idx = types.findIndex((d) => d === current)
            const newIdx = (idx + 1) % types.length
            newOptions[action.index] = {
                ...newOptions[action.index],
                strikeZone: types[newIdx],
            }
            return { ...state, chartOptions: newOptions }
        }
        case 'SET_ZOOM':
            return {
                ...state,
                zoom: action.zoom,
                translate: action.translate,
            }
        case 'RESET_CHART_SETTINGS': {
            return {
                ...state,
                settings: action.settings,
                chartOptions: action.settings.charts,
            }
        }
        default:
            return state
    }
}

export function ChartProvider({
    children,
    initialSettings,
}: {
    children: React.ReactNode
    initialSettings: Settings
}) {
    const tooltipRef = useRef<SVGGElement>(null)
    const [state, dispatch] = useReducer(chartReducer, {
        chartOptions: initialSettings.charts,
        settings: initialSettings,
        zoom: 1,
        translate: { x: 0, y: 0 },
    })

    const prevSettings = useRef<Settings>(initialSettings)

    useEffect(() => {
        const isDifferent =
            JSON.stringify(prevSettings.current) !==
            JSON.stringify(initialSettings)

        if (isDifferent) {
            dispatch({
                type: 'RESET_CHART_SETTINGS',
                settings: initialSettings,
            })
            prevSettings.current = initialSettings
        }
    }, [initialSettings])

    const value = useMemo(
        () => ({ state, dispatch, tooltipRef }),
        [state, tooltipRef]
    )

    return (
        <ChartContext.Provider value={value}>{children}</ChartContext.Provider>
    )
}

export function useChart() {
    const context = useContext(ChartContext)
    if (!context) {
        throw new Error('useChart must be used within ChartProvider')
    }
    return context
}

// Convenience hooks
export function useChartOptions(indexOverride?: number) {
    const contextIndex = useChartIndex()
    const index = indexOverride ?? contextIndex

    if (index === undefined) {
        throw new Error(
            'useChartOptions should be used within ChartIndexProvider'
        )
    }
    const { state, dispatch } = useChart()

    const chartOptions = state.chartOptions[index]

    const updateChartOptions = (updates: Partial<ChartOptions>) => {
        dispatch({ type: 'UPDATE_CHART_OPTION', index, updates })
    }

    const cycleStrikeZone = (canBePersonal: boolean) => {
        dispatch({ type: 'CYCLE_STRIKE_ZONE', index, canBePersonal })
    }

    return {
        chartOptions,
        updateChartOptions,
        cycleStrikeZone,
        settings: state.settings,
    }
}

export function useZoom() {
    const { state, dispatch } = useChart()

    return {
        zoom: state.zoom,
        translate: state.translate,
        setZoom: (zoom: number, translate: { x: number; y: number }) =>
            dispatch({ type: 'SET_ZOOM', zoom, translate }),
    }
}

export function useTooltip() {
    const { tooltipRef } = useChart()

    return {
        tooltipRef,
    }
}

export function useScales() {
    const { chartOptions, settings } = useChartOptions()

    return getScales(settings, chartOptions)
}

export function useStrikeZoneDims(optionalData?: EVPData[]) {
    const { settings } = useChartOptions()

    const data = optionalData || settings.data
    return getStrikeZoneDims('average', data)
}

export function useShouldShowNormalized() {
    const { chartOptions } = useChartOptions()

    return shouldShowNormalized(chartOptions, 'average')
}

// I'm not happy with the fuzz yet, so ditching it for now.
const hideFuzzy = true
// Check if the scale is "actual" but the strike zone is not personal
// This would trigger "fuzzy" renderings
export function useLocationAccurate_TODOFIX() {
    const showNormalized = useShouldShowNormalized()
    const isPersonal = useStrikeZoneType() === 'personal'

    return !hideFuzzy && !isPersonal && !showNormalized
}

export function useStrikeZoneType() {
    const { chartOptions, settings } = useChartOptions()

    return getStrikeZoneType(chartOptions, settings.data)
}

// Use to get the normalized feet. Doesn't pass to scales
export function useStrikeZonePositions(optionalData?: EVPData[]) {
    const { settings } = useChartOptions()
    const data = optionalData || settings.data
    const strikeZoneDims = useStrikeZoneDims(data)

    const showNormalized = useShouldShowNormalized()

    return data.map((pitch) => {
        if (pitch.x === null || pitch.y === null) {
            // throw??
            console.error('no value found')
            return { x: 0, z: 0 }
        }

        return showNormalized
            ? dynamicNormalizationFeet(strikeZoneDims, pitch)
            : { x: pitch.x, z: pitch.y }
    })
}

export function usePixelPositions(optionalData?: EVPData[]) {
    const { xScale, yScale } = useScales()
    const { settings } = useChartOptions()
    const data = optionalData || settings.data

    const zonePositions = useStrikeZonePositions(data)

    return zonePositions.map((pos) => ({ x: xScale(pos.x), y: yScale(pos.z) }))
}

// Chart Index
const ChartIndexContext = createContext<number | undefined>(undefined)

export function ChartIndexProvider({
    children,
    index,
}: {
    children: React.ReactNode
    index: number
}) {
    return (
        <ChartIndexContext.Provider value={index}>
            {children}
        </ChartIndexContext.Provider>
    )
}

function useChartIndex() {
    const index = useContext(ChartIndexContext)
    if (index === undefined) {
        throw new Error('useChartIndex must be used within ChartIndexProvider')
    }
    return index
}
